import { ErrorHandler, importProvidersFrom, inject, provideAppInitializer } from "@angular/core";
import {
	PreloadAllModules,
	provideRouter,
	withHashLocation,
	withInMemoryScrolling,
	withPreloading,
	withRouterConfig,
} from "@angular/router";
import { provideAnimations } from "@angular/platform-browser/animations";
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { AppComponent } from "app/app.component";
import { bootstrapApplication } from "@angular/platform-browser";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { StagesHttpInterceptor } from "core/http-interceptor";
import { AppLoadService } from "app/app-load.service";
import { StagesHttpRetryInterceptor } from "core/http-retry-interceptor";
import { StagesLockedHttpInterceptor } from "common/locked-http-interceptor";
import { MissingTranslationHandler, TranslateLoader, TranslateModule, TranslateParser } from "@ngx-translate/core";
import { StagesTranslateParser } from "core/translate/stages-translate.parser";
import { StagesMissingTranslationHandler } from "core/translate/missing-translation.handler";
import { APP_ROUTES } from "app/app-routes";
import { StagesErrorHandler } from "common/exception/error-handler";
import { ViewableNavigationStore } from "navigation/list/viewable-navigation-store.interface";
import { ProcessNavigationStore } from "process/navigation/process-navigation-store.logic";
import { ViewableBreadcrumbService } from "process/breadcrumb.service";
import { ProcessBreadcrumbService } from "process/process-breadcrumb.service";
import { ComponentService } from "process/view/component.service";
import { ResponsiveWidgetContainerComponent } from "process/view/responsive-widget-container.component";
import { CardDynamicComponent } from "process/view/card-dynamic.component";
import { TabCardNoRouteDynamicComponent } from "process/view/tab-card-no-route-dynamic.component";
import { ProcessIndexComponent } from "process/index/process-index.component";
import { QueryTableComponent } from "process/query/query-table.component";
import { Template12Component } from "process/view/template-12.component";
import { Template444Component } from "process/view/template-4-4-4.component";
import { Template66Component } from "process/view/template-6-6.component";
import { Template84Component } from "process/view/template-8-4.component";
import { RatingComponent } from "process/compliance/rating/rating.component";
import { ProcessFilesComponent } from "process/element/process-files.component";
import { ProcessDescriptionComponent } from "process/description/process-description.component";
import { UserAssignmentsComponent } from "process/userassignments/user-assignments.component";
import { PropertiesWrapperComponent } from "process/element/properties/properties-wrapper.component";
import { DiagramComponent } from "process/diagram/diagram.component";
import { ModelAssociationListComponent } from "process/associations/model-association-list.component";
import { ComplianceAssociationListComponent } from "process/compliance/associations/compliance-association-list.component";
import { ComplianceProcessAssociationListComponent } from "process/compliance/associations/compliance-process-association-list.component";
import { ProcessComplianceAssociationListComponent } from "process/compliance/associations/process-compliance-association-list.component";
import { ElementCommentComponent } from "process/comments/element-comment.component";
import { ActivityStreamComponent } from "process/history/activity-stream.component";
import { AddSequenceComponent } from "process/element/add/add-sequence.component";
import { FeedbackWidgetComponent } from "process/feedback/feedback-widget.component";
import { FeedbackLinkWidgetComponent } from "process/feedback/feedback-link-widget.component";
import { FeedbackSwitchWidgetComponent } from "process/feedback/feedback-switch-widget.component";
import { EnactmentAssociationListComponent } from "process/enactment/association/enactment-association-list.component";
import { EnactmentActivatorExecutionComponent } from "process/enactment/execution/enactment-activator-execution.component";
import { PhaseFreezeOverviewComponent } from "process/freeze/overview/phase-freeze-overview.component";
import { ProcessTableComponent } from "process/table/process-table.component";
import { ProcessListComponent } from "process/table/process-list.component";
import { ProcessDocumentComponent } from "process/table/process-document.component";
import { NotificationComponent } from "process/notification/notification.component";
import { UserGroupsComponent } from "process/usergroups/user-groups.component";
import { DependentElementsListComponent } from "process/dependant-elements/dependent-elements-list.component";
import { TailoringConditionsGroupedComponent } from "process/tailoring-conditions/tailoring-conditions-grouped.component";
import { DependentElementConfirmDialogComponent } from "process/dependant-elements/dependent-element-confirm-dialog.component";

export function translateHttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
	return new TranslateHttpLoader(http, "app/messageResources/", "");
}

export function initializeApp() {
	const appLoadService = inject(AppLoadService);
	return appLoadService.initializeLanguage();
}

export const provideTranslation = () => ({
	loader: {
		provide: TranslateLoader,
		useFactory: translateHttpLoaderFactory,
		deps: [HttpClient],
	},
	parser: {
		provide: TranslateParser,
		useExisting: StagesTranslateParser,
	},
	missingTranslationHandler: {
		provide: MissingTranslationHandler,
		useExisting: StagesMissingTranslationHandler,
	},
	useDefaultLang: false,
});

const viewComponents = {
	ResponsiveWidgetContainerComponent: ResponsiveWidgetContainerComponent,
	card: CardDynamicComponent,
	tabbedcard: TabCardNoRouteDynamicComponent,
	IndexComponent: ProcessIndexComponent,
	QueryTableComponent: QueryTableComponent,
	Template12Component: Template12Component,
	Template444Component: Template444Component,
	Template66Component: Template66Component,
	Template84Component: Template84Component,
	RatingComponent: RatingComponent,
	FilesComponent: ProcessFilesComponent,
	ProcessDescriptionComponent: ProcessDescriptionComponent,
	UserAssignmentsComponent: UserAssignmentsComponent,
	PropertiesComponent: PropertiesWrapperComponent,
	DiagramComponent: DiagramComponent,
	AssociationListComponent: ModelAssociationListComponent,
	ComplianceAssociationListComponent: ComplianceAssociationListComponent,
	ComplianceProcessAssociationListComponent: ComplianceProcessAssociationListComponent,
	ProcessComplianceAssociationListComponent: ProcessComplianceAssociationListComponent,
	ElementCommentComponent: ElementCommentComponent,
	ActivityStreamComponent: ActivityStreamComponent,
	AddSequenceComponent: AddSequenceComponent,
	FeedbackWidgetComponent: FeedbackWidgetComponent,
	FeedbackLinkWidgetComponent: FeedbackLinkWidgetComponent,
	FeedbackSwitchWidgetComponent: FeedbackSwitchWidgetComponent,
	EnactmentAssociationListComponent: EnactmentAssociationListComponent,
	EnactmentActivatorExecutionComponent: EnactmentActivatorExecutionComponent,
	PhaseFreezeOverviewComponent: PhaseFreezeOverviewComponent,
	ProcessTableComponent: ProcessTableComponent,
	ProcessListComponent: ProcessListComponent,
	ProcessDocumentComponent: ProcessDocumentComponent,
	NotificationComponent: NotificationComponent,
	UserGroupsComponent: UserGroupsComponent,
	DependentElementsListComponent: DependentElementsListComponent,
	TailoringConditionsComponent: TailoringConditionsGroupedComponent,
	DependentElementConfirmDialogComponent: DependentElementConfirmDialogComponent,
};

bootstrapApplication(AppComponent, {
	providers: [
		// TODO check why the viewComponents need to be provided here and not in the process_routes
		ComponentService,
		{
			provide: "ViewComponents",
			useValue: viewComponents,
		},

		{ provide: ViewableNavigationStore, useClass: ProcessNavigationStore },
		{ provide: ViewableBreadcrumbService, useClass: ProcessBreadcrumbService },
		provideAnimations(),
		provideHttpClient(withInterceptorsFromDi()),
		importProvidersFrom([TranslateModule.forRoot(provideTranslation())]),

		{ provide: HTTP_INTERCEPTORS, useClass: StagesHttpInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: StagesHttpRetryInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: StagesLockedHttpInterceptor, multi: true },
		{ provide: ErrorHandler, useClass: StagesErrorHandler },
		provideAppInitializer(() => initializeApp()),
		provideRouter(
			APP_ROUTES,
			// withDebugTracing(),
			withPreloading(PreloadAllModules),
			withHashLocation(),
			withRouterConfig({
				paramsInheritanceStrategy: "always",
				onSameUrlNavigation: "reload",
			}),
			withInMemoryScrolling({ scrollPositionRestoration: "top" }),
		),
	],
});
